import { UploadType } from '../../../../__generated__/gql/zeus/index.ts';

import { generateUploadUrlMutationRequest } from '../../../accounts/requests/generate-upload-url.mutation.ts';

import { DEFAULT_AUDIO_MIME_TYPE } from '../../constants/audio.ts';
import { LessonTaskStepKeys } from '../../constants/lesson-task-step-keys.ts';

import { s3RequestClientUtil } from '../clients/s3.client.util.ts';
import { getSentryUtil } from '../monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../redux-store.util.ts';

export interface PrepareUploadResponse {
  uploadUrl: string;
  key: string;
}

export class AudioRecordingUploaderUtil {
  private uploadUrl: string | null = null;

  async prepareUpload(): Promise<PrepareUploadResponse> {
    const store = await getReduxStoreUtil().getRootState();
    const profileId = store.accounts.currentProfile?.id;

    if (!profileId) {
      throw new Error('No profile ID found');
    }

    const { lesson, task, step } = store.lesson.currentLessonTaskStepKey ?? LessonTaskStepKeys.DEFAULT_CURRENT;
    const response = await generateUploadUrlMutationRequest({
      contentType: DEFAULT_AUDIO_MIME_TYPE,
      uploadType: UploadType.USER_SOUND,
      profileId,
      metadata: JSON.stringify({
        lesson,
        task,
        step,
      }),
    });

    if (!response.success) {
      throw new Error('Failed to generate upload URL');
    }

    this.uploadUrl = response.data.presignedUrl;
    return {
      uploadUrl: this.uploadUrl,
      key: response.data.key,
    };
  }

  async upload(audioRecording?: Blob): Promise<void> {
    if (!audioRecording) {
      return;
    }

    const presignedUrl = this.uploadUrl ?? (await this.prepareUpload()).uploadUrl;

    const response = await s3RequestClientUtil.put(presignedUrl, audioRecording, {
      headers: {
        'Content-Type': audioRecording.type,
      },
    });

    if (response.status !== 200) {
      getSentryUtil().trackError(new Error('Failed to upload audio recording'), {});
    }

    this.uploadUrl = null;
  }
}
